import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { StoreState } from '../../store/store.interface';
import '../search-v2/search-v2';
import '../search/search-results-sorting';
import '../search/search';
import { DEFAULT_SEARCH_TERM_REDIRECTS, SearchTermRedirects, Size } from '../search/search';
import { getSearchLabel } from './utils/tabbed-search.utils';
import { isSearchPage } from '../../common/utils/util';

/**
 * Tabbed Search Component
 *
 * This component provides a tabbed interface for different search experiences:
 * - Product search
 * - Support search (V2)
 * - Articles search
 *
 * It renders the appropriate search component based on the active tab.
 */
@customElement('mhecomm-tabbed-search')
export class TabbedSearch extends connect(store)(BaseComponent) {
  // Search placeholder texts
  @property()
  placeholderProduct = 'Search by ISBN, Title, Author, Keyword';

  @property()
  placeholderSupport = 'Search by Title, Author, Keyword';

  @property()
  placeholderArticle = 'Search by Title, Author, Keyword';

  // Search behavior configuration
  @property()
  autoLoad = 'true';

  @property({ type: Number })
  pageSize = 10;

  @property()
  size = Size.DEFAULT;

  @property()
  lightMode = 'false';

  @property()
  catalog = 'unified';

  @property()
  store: string | undefined;

  @property()
  suggestionsOpen = 'true';

  @property()
  useSuggestions = 'true';

  @property()
  loadResults = 'true';

  @property()
  searchPageType = '';

  // Search term redirects configuration
  @property({ type: Object })
  searchTermRedirects: SearchTermRedirects = DEFAULT_SEARCH_TERM_REDIRECTS;

  // Track active tab
  @state()
  private activeTab = 'Product';

  /**
   * Redux state change handler
   * Updates the component when active tab changes in the store
   */
  stateChanged(state: StoreState) {
    const newTab = state.tabbedSearchActiveTabState.activeTabKey;
    if (this.activeTab !== newTab) {
      this.activeTab = newTab;
      this.requestUpdate();
    }
  }

  /**
   * Main render function
   */
  renderComp() {
    return html`
      <div role="search" aria-label="${getSearchLabel(this.activeTab)}">${this.renderSearchComponents()}</div>
    `;
  }

  /**
   * Conditionally renders the appropriate search component based on active tab
   */
  private renderSearchComponents() {
    switch (this.activeTab) {
      case 'Product':
        return this.renderSearch(this.placeholderProduct, 'PRODUCTS');
      case 'Support':
        return this.renderSearchV2();
      case 'Articles':
        return this.renderSearch(this.placeholderArticle, 'ARTICLES');
      default:
        return nothing;
    }
  }

  /**
   * Returns false when on the search page, because SearchV3 is the coordinator
   * for all searches there. Sub-components should not auto-load.
   * Uses the centralized `isSearchPage()` util so both `/search` and
   * `/search.html` URLs are handled consistently.
   */
  private get effectiveAutoLoad(): string {
    if (isSearchPage()) {
      return 'false';
    }
    return this.autoLoad;
  }

  /**
   * Renders the Support search component (V2)
   */
  private renderSearchV2() {
    return html`
      <mhecomm-search-v2
        id="mh-ecomm-support-search"
        placeholder="${this.placeholderSupport}"
        autoload="${this.effectiveAutoLoad}"
        pagesize="${this.pageSize}"
        size="${this.size}"
        lightmode="${this.lightMode}"
        catalog="UNIFIED"
        suggestionsopen="${this.suggestionsOpen}"
        usesuggestions="${this.useSuggestions}"
        loadresults="${this.loadResults}"
        .searchtermredirects="${this.searchTermRedirects}"
        searchpagetype="${this.searchPageType}"
        data-automation-id="search-v2-component"
        data-search-type="support"></mhecomm-search-v2>
    `;
  }

  /**
   * Renders the Product/Articles search component
   *
   * @param placeholder - The search box placeholder text
   * @param catalog - The catalog to search within (PRODUCTS or ARTICLES)
   */
  private renderSearch(placeholder: string, catalog: string) {
    const searchType = catalog.toLowerCase();
    return html`
      <mhecomm-search
        id="mh-ecomm-${searchType}-search"
        placeholder="${placeholder}"
        autoload="${this.effectiveAutoLoad}"
        pagesize="${this.pageSize}"
        size="${this.size}"
        lightmode="${this.lightMode}"
        catalog="${catalog}"
        suggestionsopen="${this.suggestionsOpen}"
        usesuggestions="${this.useSuggestions}"
        loadresults="${this.loadResults}"
        .searchTermRedirects="${this.searchTermRedirects}"
        searchpagetype="${this.searchPageType}"
        data-automation-id="search-component"
        data-search-type="${searchType}"></mhecomm-search>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tabbed-search': TabbedSearch;
  }
}
